import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import {
  BsLightningChargeFill, BsShieldFillCheck, BsStarFill,
  BsTruckFrontFill, BsTools, BsCurrencyRupee
} from 'react-icons/bs';
import { IoFlash } from 'react-icons/io5';
import { FiArrowRight, FiArrowRightCircle, FiPlay } from 'react-icons/fi';
import { MdClose, MdCable, MdElectricBolt, MdOutlineToggleOn, MdOutlineSettings } from 'react-icons/md';
import { RiShieldCheckFill } from 'react-icons/ri';

/* =========================================================
   SLIDER DATA
   ========================================================= */
const SLIDES = [
  {
    cls: 'slide--1',
    badge: 'ISI Certified Products',
    titleLine1: 'QUALITY',
    titleLine2: <span className="text-grad-cyan">WIRING</span>,
    titleLine3: 'MATERIALS',
    desc: 'House wires, armoured cables, LAN cables, flexible wires — all ISI certified and available in every gauge at Spark Zone.',
    btn1: { label: 'Shop Now', to: '/products', icon: <IoFlash /> },
    btn2: { label: 'View Catalogue', to: '/products', icon: <FiArrowRight /> },
  },
  {
    cls: 'slide--2',
    badge: 'Trusted Since 2012',
    titleLine1: 'COMPLETE',
    titleLine2: <span className="text-grad-amber">SWITCHGEAR</span>,
    titleLine3: '& PROTECTION',
    desc: 'DB boxes, MCBs, RCCBs, isolators, and busbars — everything you need for a safe and compliant electrical panel.',
    btn1: { label: 'Browse Products', to: '/products', icon: <IoFlash /> },
    btn2: { label: 'Our Services', to: '/services', icon: <FiArrowRight /> },
  },
  {
    cls: 'slide--3',
    badge: 'Full Electrical Fit-Out',
    titleLine1: 'CONDUIT,',
    titleLine2: <span className="text-grad-teal">SWITCHES</span>,
    titleLine3: '& MORE',
    desc: 'PVC conduits, modular switches, earthing rods, insulation tape — one stop for every item on your electrical BOM.',
    btn1: { label: 'See All Products', to: '/products', icon: <IoFlash /> },
    btn2: { label: 'Get Free Quote', to: '/contact', icon: <FiArrowRight /> },
  },
];

function HeroSlider() {
  const [cur, setCur] = useState(0);
  const total = SLIDES.length;
  const next = useCallback(() => setCur(c => (c + 1) % total), [total]);
  useEffect(() => { const t = setInterval(next, 5200); return () => clearInterval(t); }, [next]);

  return (
    <section className="hero">
      <div className="hero-bg-grid" />
      <div className="hero-scan" />
      <div className="hero-orb hero-orb-1" />
      <div className="hero-orb hero-orb-2" />
      <div className="spark-dots">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="spark-dot" style={{ left:`${8+i*9}%`, top:`${18+(i%3)*26}%`, animationDelay:`${i*0.4}s` }} />
        ))}
      </div>
      <div className="slider">
        {SLIDES.map((s, i) => (
          <div key={i} className={`slide ${s.cls}${i === cur ? ' active' : ''}`}>
            <div className="slide-bg" />
            <div className="slide-inner">
              <div>
                <div className="slide-badge"><span className="slide-badge-dot" />{s.badge}</div>
                <h1 className="slide-title">{s.titleLine1}<br />{s.titleLine2}<br />{s.titleLine3}</h1>
                <p className="slide-desc">{s.desc}</p>
                <div className="slide-actions">
                  <Link to={s.btn1.to} className="btn-primary">{s.btn1.icon}{s.btn1.label}</Link>
                  <Link to={s.btn2.to} className="btn-ghost">{s.btn2.label}{s.btn2.icon}</Link>
                </div>
              </div>
              <div className="slide-visual">
                <div className="bolt-wrap">
                  <div className="bolt-ring" /><div className="bolt-ring" /><div className="bolt-ring" />
                  <div className="bolt-core">
                    <BsLightningChargeFill style={{ fontSize:'5rem', color:'var(--primary)', filter:'drop-shadow(0 0 30px var(--primary))' }} />
                  </div>
                  <div className="bolt-dot bd-1" /><div className="bolt-dot bd-2" /><div className="bolt-dot bd-3" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="slider-dots" style={{ position:'absolute', bottom:22, left:'50%', transform:'translateX(-50%)', zIndex:5, display:'flex', gap:8 }}>
        {SLIDES.map((_, i) => (
          <div key={i} className={`slider-dot${i === cur ? ' active' : ''}`} onClick={() => setCur(i)} />
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   MARQUEE
   ========================================================= */
const MARQUEE_ITEMS = [
  'Wiring & Cables', 'Conduit & Piping', 'Switchgear', 'Modular Switches',
  'MCB & RCCB', 'Distribution Boards', 'Earthing Material', 'Lighting Points',
  'Fan Regulators', 'Insulation Tape', 'LAN & TV Cables', 'Plug Tops',
];
function Marquee() {
  const d = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="marquee-wrap">
      <div className="marquee-track">
        {d.map((it, i) => (
          <span key={i} className="marquee-item"><BsLightningChargeFill /> {it}</span>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
   ANIMATED VIDEO SECTION
   ========================================================= */
function VideoSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const YOUTUBE_ID = 'LXb3EKWsInQ';

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setModalOpen(false); };
    if (modalOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [modalOpen]);

  return (
    <>
      <section style={{
        background: 'var(--navy)',
        padding: '100px 0',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none' }}>
          <div style={{
            position:'absolute', width:500, height:500, borderRadius:'50%',
            background:'radial-gradient(circle, rgba(13,148,136,0.18), transparent 70%)',
            top:-150, left:-100, filter:'blur(80px)',
            animation:'float 8s ease-in-out infinite',
          }} />
          <div style={{
            position:'absolute', width:400, height:400, borderRadius:'50%',
            background:'radial-gradient(circle, rgba(94,234,212,0.1), transparent 70%)',
            bottom:-100, right:-80, filter:'blur(80px)',
            animation:'float 10s ease-in-out infinite reverse',
          }} />
          <div style={{
            position:'absolute', inset:0,
            backgroundImage:'linear-gradient(rgba(13,148,136,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(13,148,136,0.05) 1px,transparent 1px)',
            backgroundSize:'60px 60px',
          }} />
        </div>

        <div className="container vs-responsive-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'70px', alignItems:'center', position:'relative', zIndex:2 }}>
          {/* Left: text */}
          <div>
            <div className="section-subtitle">⚡ See Us In Action</div>
            <h2 className="section-title" style={{ marginBottom:22 }}>
              TRUSTED BY <span>THOUSANDS</span> ACROSS GUJARAT
            </h2>
            <p style={{ color:'var(--gray-400)', lineHeight:1.8, marginBottom:32, fontSize:'.95rem' }}>
              From single-room wiring to complete factory electrical fit-outs — Spark Zone supplies ISI-certified materials and expert installation services across Ahmedabad and Gujarat.
            </p>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
              {[
                { num:'12+', label:'Years in Business' },
                { num:'15K+', label:'Happy Customers' },
                { num:'98%', label:'Satisfaction Rate' },
              ].map((s, i) => (
                <div key={i} style={{
                  padding:'18px 24px', background:'rgba(255,255,255,0.04)',
                  border:'1px solid rgba(255,255,255,0.08)', borderRadius:'var(--radius)',
                  textAlign:'center',
                }}>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'2rem', color:'var(--primary)', letterSpacing:2 }}>{s.num}</div>
                  <div style={{ fontSize:'.78rem', color:'var(--gray-400)', fontFamily:"'Rajdhani',sans-serif", fontWeight:600, letterSpacing:1 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Right: play button */}
          <div style={{ display:'flex', justifyContent:'center' }}>
            <div
              onClick={() => setModalOpen(true)}
              style={{
                position:'relative', width:280, height:280, borderRadius:'50%', cursor:'pointer',
                background:'radial-gradient(circle at 40% 40%, rgba(13,148,136,0.25), rgba(10,22,40,0.8))',
                border:'2px solid rgba(13,148,136,0.4)',
                display:'flex', alignItems:'center', justifyContent:'center',
                transition:'var(--transition)',
                boxShadow:'0 0 60px rgba(13,148,136,0.15)',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(13,148,136,0.8)'; e.currentTarget.style.boxShadow='0 0 80px rgba(13,148,136,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(13,148,136,0.4)'; e.currentTarget.style.boxShadow='0 0 60px rgba(13,148,136,0.15)'; }}
            >
              <div style={{
                width:72, height:72, borderRadius:'50%',
                background:'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 0 30px rgba(245,166,35,0.5)',
              }}>
                <FiPlay style={{ fontSize:'1.8rem', color:'#000', marginLeft:4 }} />
              </div>
              <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', borderRadius:'50%', border:'2px solid rgba(13,148,136,0.3)', width:320, height:320, animation:'vsRingPulse 2.5s ease-in-out infinite' }} />
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes vsRingPulse {
          0%   { transform: translate(-50%,-50%) scale(0.8); opacity: 0.8; }
          100% { transform: translate(-50%,-50%) scale(1.3); opacity: 0; }
        }
        @media (max-width: 860px) {
          .vs-responsive-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
        }
      `}</style>

      {modalOpen && (
        <div
          style={{
            position:'fixed', inset:0, zIndex:9999,
            background:'rgba(10,22,40,0.93)', backdropFilter:'blur(12px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:20,
            animation:'fadeInUp 0.3s ease forwards',
          }}
          onClick={() => setModalOpen(false)}
        >
          <div
            style={{ position:'relative', width:'100%', maxWidth:900, animation:'zoomIn 0.35s ease forwards' }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setModalOpen(false)}
              style={{
                position:'absolute', top:-48, right:0,
                width:40, height:40, borderRadius:'50%',
                background:'rgba(255,255,255,0.1)',
                border:'1.5px solid rgba(255,255,255,0.2)',
                color:'white', display:'flex', alignItems:'center', justifyContent:'center',
                cursor:'pointer', transition:'var(--transition)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background='var(--primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.1)'; }}
            >
              <MdClose style={{ fontSize:'1.4rem' }} />
            </button>
            <div style={{
              position:'relative', width:'100%', aspectRatio:'16/9',
              borderRadius:'var(--radius-xl)', overflow:'hidden',
              border:'2px solid rgba(13,148,136,0.3)',
              boxShadow:'0 0 80px rgba(13,148,136,0.2)',
            }}>
              <iframe
                src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0`}
                title="Spark Zone — Our Work"
                style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none' }}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* =========================================================
   HIGHLIGHTS
   ========================================================= */
const highlights = [
  { icon: <RiShieldCheckFill />, title: 'ISI Certified Products',  desc: 'Every product we stock meets BIS and ISI safety standards — no substandard goods, ever.' },
  { icon: <BsTruckFrontFill />,  title: 'Same-Day Delivery',       desc: 'Order before 3PM and get your items delivered the same day within Ahmedabad.' },
  { icon: <BsTools />,           title: 'Expert Installation',     desc: 'Certified electricians available for home and commercial wiring setups.' },
  { icon: <BsCurrencyRupee />,   title: 'Best Price Guarantee',    desc: 'We match any lower price in Ahmedabad — no questions asked.' },
];

const featuredProducts = [
  { icon: <MdCable />,           name: 'House Wires & Cables',       desc: 'FR copper wires (1–6mm²), armoured cables, LAN & coaxial cables. All gauges in stock.', badge: 'bestseller' },
  { icon: <MdElectricBolt />,    name: 'MCB, RCCB & DB Panels',      desc: 'ISI-marked miniature circuit breakers, RCCBs, and distribution boards for safe circuits.', badge: null },
  { icon: <MdOutlineToggleOn />, name: 'Modular Switches & Sockets', desc: '6A/16A switches, 3-pin & 5-pin sockets, fan regulators, and concealed gang boxes.', badge: 'popular' },
  { icon: <MdOutlineSettings />, name: 'Conduit & Fittings',         desc: 'PVC conduit pipes, flexible pipes, junction boxes, bends, couplers, and tees.', badge: null },
];

export default function Home() {
  return (
    <div className="page-wrapper">
      <HeroSlider />
      <Marquee />

      {/* Featured Products */}
      <section className="section">
        <div className="container">
          <div className="section-header" style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', flexWrap:'wrap', gap:20 }}>
            <div>
              <div className="section-subtitle">Top Picks</div>
              <h2 className="section-title">FEATURED <span>PRODUCTS</span></h2>
            </div>
            <Link to="/products" className="btn-ghost" style={{ marginBottom:14 }}>
              View All <FiArrowRightCircle />
            </Link>
          </div>
          <div className="products-grid">
            {featuredProducts.map((p, i) => (
              <div className="product-card" key={i} style={{ animationDelay:`${i*0.1}s`, display:'flex', flexDirection:'column' }}>
                {p.badge && <span className={`product-badge ${p.badge}`}>{p.badge}</span>}
                <div className="product-icon">{p.icon}</div>
                <div className="product-name">{p.name}</div>
                <p className="product-desc" style={{ flex:1 }}>{p.desc}</p>
                <div className="product-footer">
                  <div className="product-stars">{[...Array(5)].map((_,j) => <BsStarFill key={j} />)}</div>
                  <button className="product-enquire"><IoFlash /> Enquire</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO SECTION */}
      <VideoSection />

      {/* Highlights */}
      <section className="section" style={{ background:'rgba(255,255,255,0.015)' }}>
        <div className="container">
          <div className="section-header" style={{ textAlign:'center' }}>
            <div className="section-subtitle">Why Choose Us</div>
            <h2 className="section-title">BUILT ON <span>TRUST</span></h2>
          </div>
          <div className="highlights-grid">
            {highlights.map((h, i) => (
              <div key={i} className="highlight-card" style={{ animationDelay:`${i*0.12}s` }}>
                <div className="highlight-icon">{h.icon}</div>
                <div className="highlight-title">{h.title}</div>
                <p className="highlight-desc">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="brands-section">
        <div className="container">
          <div className="brands-label">Trusted Brands We Carry</div>
          <div className="brands-marquee">
            <div className="brands-track">
              {[
                'ABB', 'ANCHOR', 'APAR INDUSTRIES', 'BAJAJ ELECTRICALS', 'BHEL',
                'C&S ELECTRIC', 'CG POWER', 'CROMPTON GREAVES', 'FINOLEX',
                'GM MODULAR', 'GOLDMEDAL', 'HAVELLS', 'HI-FI', 'HITACHI ENERGY',
                'HPL ELECTRIC', 'KEI INDUSTRIES', 'L&T', 'LEGRAND',
                'ORIENT ELECTRIC', 'POLYCAB', 'ROMA', 'RR KABEL',
                'SCHNEIDER', 'SIEMENS', 'SYSKA', 'V-GUARD', 'WIPRO NORTH-WEST'
              ].map((b, i) => (
                <div className="brand-item" key={i}><BsLightningChargeFill /> {b}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="tag" style={{ marginBottom:20 }}><span className="tag-dot" /> Visit Us Today</div>
          <h2 className="cta-title">READY TO START YOUR <span style={{ color:'var(--primary)' }}>WIRING PROJECT?</span></h2>
          <p className="cta-desc">Get a free material estimate and expert advice from our team. No obligation — just honest guidance.</p>
          <div className="cta-actions">
            <Link to="/contact" className="btn-primary"><IoFlash /> Get Free Quote</Link>
            <Link to="/products" className="btn-outline"><FiArrowRight /> Browse Products</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}